/**
 * Variante grafica dell'area post-login.
 *
 * Il collaudo e' finito: resta solo `stitch` (sidebar chiara, barra superiore
 * in vetro, righe a pillola). Le varianti `classic`, `mista` e `studio` sono
 * disabilitate — il selettore non le espone piu' e una preferenza vecchia in
 * `localStorage` viene ignorata, non ripristinata.
 *
 * Il provider resta perche' e' quello che mette `data-ui` sull'elemento radice:
 * senza attributo i token di stitch.css non si applicano. Il giorno in cui non
 * servira' piu' nessun altro ramo, questo file e Layout/Shell spariscono.
 */
import { createContext, useContext, useEffect, useMemo } from 'react';

export type UiVariant = 'stitch';

/** Unica grafica attiva. Il selettore con una voce sola non si mostra. */
export const UI_VARIANTS: ReadonlyArray<{ id: UiVariant; label: string; hint: string }> = [
  { id: 'stitch', label: 'Stitch', hint: 'Vetro e pillole, barra laterale chiara' },
];

/*
 * Niente piu' lettura da `localStorage`: con una variante sola la vecchia
 * preferenza di collaudo ('classic', 'studio', ...) porterebbe a una grafica
 * che non esiste piu'. La chiave 'mm_ui' resta orfana e innocua.
 */
const ONLY_VARIANT: UiVariant = 'stitch';

type Ctx = { variant: UiVariant; setVariant: (v: UiVariant) => void };

const UiVariantContext = createContext<Ctx | null>(null);

export function UiVariantProvider({ children }: { children: React.ReactNode }) {
  // Niente stato: la variante e' una costante. L'attributo sull'elemento radice
  // resta l'aggancio del CSS (`:root[data-ui='stitch']`), va scritto lo stesso.
  useEffect(() => {
    document.documentElement.dataset['ui'] = ONLY_VARIANT;
  }, []);

  // `setVariant` resta nel contesto per non toccare i consumatori: non fa nulla.
  const value = useMemo<Ctx>(() => ({ variant: ONLY_VARIANT, setVariant: () => {} }), []);
  return <UiVariantContext.Provider value={value}>{children}</UiVariantContext.Provider>;
}

export function useUiVariant(): Ctx {
  const ctx = useContext(UiVariantContext);
  if (!ctx) throw new Error('useUiVariant fuori da UiVariantProvider');
  return ctx;
}
